import { useState } from "react";
import { Link } from "react-router-dom";
import { allPhotos, categories } from "../../mocks/photos";

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [selected, setSelected] = useState<number | null>(null);

  const filtered =
    activeCategory === "Todos"
      ? allPhotos
      : allPhotos.filter((p) => p.category === activeCategory);

  const selectedPhoto = allPhotos.find((p) => p.id === selected) ?? null;
  const filteredIds = filtered.map((p) => p.id);

  const goNext = () => {
    if (!selected) return;
    const idx = filteredIds.indexOf(selected);
    setSelected(filteredIds[(idx + 1) % filteredIds.length]);
  };

  const goPrev = () => {
    if (!selected) return;
    const idx = filteredIds.indexOf(selected);
    setSelected(filteredIds[(idx - 1 + filteredIds.length) % filteredIds.length]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-teal-700 pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-100 hover:text-white text-sm font-medium mb-6 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Volver al inicio
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-water-flash-fill text-white text-lg" />
            </div>
            <span className="text-teal-200 text-xs font-bold tracking-[0.3em] uppercase">INSERCOM</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-widest mb-3">GALERÍA</h1>
          <p className="text-teal-100 text-base max-w-xl">
            Explora todos nuestros proyectos terminados — piscinas residenciales, hidromasajes y construcciones especializadas en República Dominicana.
          </p>
          <p className="text-teal-300 text-sm mt-2 font-medium">{allPhotos.length} proyectos</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-3 overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                activeCategory === cat
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700"
              }`}
            >
              {cat}
              <span className="ml-2 text-xs opacity-70">
                {cat === "Todos"
                  ? allPhotos.length
                  : allPhotos.filter((p) => p.category === cat).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((photo, i) => (
            <div
              key={photo.id}
              className={`relative overflow-hidden rounded-xl group cursor-pointer ${
                i === 0 ? "col-span-2 row-span-2 h-80 md:h-96" : "h-56"
              }`}
              onClick={() => setSelected(photo.id)}
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <img
                src={photo.src}
                alt={photo.label}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                <span className="text-white font-semibold text-sm">{photo.label}</span>
                <span className="text-teal-200 text-xs bg-teal-800/60 px-2 py-1 rounded-full">{photo.category}</span>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <i className="ri-zoom-in-line text-white text-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-24 text-gray-400">
            <i className="ri-image-2-line text-5xl mb-4 block" />
            <p>No hay fotos en esta categoría</p>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-teal-700 py-16 px-6 text-center">
        <h2 className="text-3xl font-black text-white mb-3">¿Te gustó lo que viste?</h2>
        <p className="text-teal-100 mb-8 max-w-md mx-auto">Contáctanos y hagamos realidad tu proyecto de piscina o hidromasaje.</p>
        <Link
          to="/#contacto"
          className="inline-block bg-white text-teal-700 font-bold px-8 py-3 rounded-full hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          Solicitar Cotización
        </Link>
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-2xl overflow-hidden bg-black">
              <img
                src={selectedPhoto.src}
                alt={selectedPhoto.label}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            </div>
            <div className="flex items-center justify-between mt-4 px-2">
              <div>
                <p className="text-white font-bold text-lg">{selectedPhoto.label}</p>
                <span className="text-teal-300 text-sm">{selectedPhoto.category}</span>
              </div>
              <p className="text-gray-400 text-sm">
                {filteredIds.indexOf(selected!) + 1} / {filteredIds.length}
              </p>
            </div>

            <button
              onClick={() => setSelected(null)}
              className="absolute -top-4 -right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <button
              onClick={goPrev}
              className="absolute left-0 top-1/2 -translate-x-14 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-0 top-1/2 translate-x-14 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}